import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PageTitle } from '@/layout/page-title';
import { cn } from '@/lib/utils';
import { createFileRoute, Link, useLoaderData, useRouter } from '@tanstack/react-router'
import { ArrowLeft, CheckIcon, ChevronsUpDownIcon, Loader2, TrashIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from "@hookform/resolvers/zod"
import toast from 'react-hot-toast';
import { LoadingSpinner } from '@/components/ui/spinner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getTaskDetails, getTasksProps, putUpdateActivity } from '@/tasks';
import { requireAuth } from '@/auth/requireAuth';
import { CompanySelector } from '@/components/company/company-selector';
import type { Company } from '@/company';
import { DatePickerWithPresets } from '@/components/tasks/date-select';
import { Checkbox } from '@/components/ui/checkbox';
import { TaskAddAssignee } from '@/components/tasks/task-add-assignee';
import { deleteRole, getPeople } from '@/people';

export const Route = createFileRoute('/(app)/_layout/tasks/$id')({
  beforeLoad: requireAuth,
  component: RouteComponent,
  loader: async (opts) => {
    const id = opts.params.id as string;
    const taskDetailsResult = await getTaskDetails(id);
    const taskPropsResult = await getTasksProps();
    const peopleResult = await getPeople();
    return { taskDetailsResult, taskPropsResult, peopleResult };
  }
})

function RouteComponent() {
  const [openScope, setOpenScope] = useState(false);
  const [openDescription, setOpenDescription] = useState(false);
  const [openRole, setOpenRole] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const { taskDetailsResult, taskPropsResult, peopleResult } = useLoaderData({ from: '/(app)/_layout/tasks/$id' });

  const data = taskPropsResult.data;
  const task = taskDetailsResult.data;
  const people = peopleResult.data;

  const formSchema = z.object({
    id: z.string(),
    scope: z.string(),
    description: z.string(),
    role: z.string(),
    companyId: z.string(),
    deadline: z.date(),
    reminder1: z.date(),
    reminder2: z.date(),
    keyDeadline: z.boolean()
  })
  const router = useRouter();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: task!.id,
      scope: task!.scope,
      description: task!.description,
      role: task!.role,
      companyId: task!.companyId,
      deadline: new Date(task!.deadline),
      reminder1: new Date(task!.reminder1),
      reminder2: new Date(task!.reminder2),
      keyDeadline: false,
    },
    mode: 'onChange',

  })

  const handleDeleteRole = async (personName: string) => {
    const personEmail = people?.find(person => person.name === personName)?.email;
    if (!personEmail) {
      toast.error("Person not found.");
      return;
    }
    setLoadingDelete(true)
    const { error } = await deleteRole({
      companyId: task!.companyId,
      role: task!.role,
      personEmail
    })
    setLoadingDelete(false)
    if (error) {
      toast.error("Failed to delete assignee.")
      return
    }
    toast.success("Assignee deleted successfully.")
    router.invalidate();
  }

  async function onSubmit(values: z.infer<typeof formSchema>) {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    setLoading(true)
    const date = {
      scope: values.scope,
      description: values.description,
      role: values.role,
      companyId: values.companyId,
      deadline: values.deadline.toISOString(),
      reminder1: values.reminder1 ? values.reminder1.toISOString() : null,
      reminder2: values.reminder2 ? values.reminder2.toISOString() : null,
      keyDeadline: values.keyDeadline,
    }
    const { error } = await putUpdateActivity(values.id, date);
    setLoading(false)
    if (error) {
      toast.error("Error updating task: " + error);
      return;
    }
    toast.success("Task updated successfully!");
    router.invalidate();
  }

  return (
    <div className="container max-w-[1900px] mx-auto py-10 px-4">
      <div className="space-y-4">
        <PageTitle title={`Edit Task`}
          leftActions={
            <Button asChild variant={'ghost'}>
              <Link to="/manage-tasks" className="text-sm">
                <ArrowLeft className="mr-2" />
                Back to Manage Tasks
              </Link>
            </Button>
          }
        />

        <div className="grid grid-cols-3 gap-2">

          <Card className="md:col-span-2 sm:col-span-3">
            <CardHeader>

              <CardTitle>Task Information</CardTitle>
            </CardHeader>
            <CardContent>
              <Form {...form} >
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                  <div className="grid grid-cols-3 gap-4">

                    <div>

                      <FormField
                        control={form.control}
                        name="scope"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Scope</FormLabel>
                            <FormControl>
                              <Popover open={openScope} onOpenChange={setOpenScope}>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={openScope}
                                    className="w-full h-8 justify-between overflow-hidden"
                                  >
                                    {field.value ? field.value
                                      : "Select scope..."}
                                    <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[300px] p-0">
                                  <Command>
                                    <CommandInput placeholder="Search scope..." />
                                    <CommandList>
                                      <CommandEmpty>No scope found.</CommandEmpty>
                                      <CommandGroup>
                                        {data!.scopes.map((scope: string) => (
                                          <CommandItem
                                            key={scope}
                                            value={scope}
                                            onSelect={() => {
                                              field.onChange(scope)
                                              setOpenScope(false)  // Close the popover after selection
                                            }
                                            }

                                          >
                                            <CheckIcon
                                              className={cn(
                                                "mr-2 h-4 w-4",
                                                field.value === scope ? "opacity-100" : "opacity-0"
                                              )}
                                            />
                                            {scope}
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </FormControl>
                            {/* <FormDescription>Select the manager's email from the list.</FormDescription> */}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div>
                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                              <Popover open={openDescription} onOpenChange={setOpenDescription}>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={openDescription}
                                    className="w-full h-8 justify-between overflow-hidden"
                                  >
                                    {field.value ? field.value
                                      : "Select description..."}
                                    <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[300px] p-0">
                                  <Command>
                                    <CommandInput placeholder="Search description..." />
                                    <CommandList>
                                      <CommandEmpty>No description found.</CommandEmpty>
                                      <CommandGroup>
                                        {data!.descriptions.map((description: string) => (
                                          <CommandItem
                                            key={description}
                                            value={description}
                                            onSelect={() => {
                                              field.onChange(description)
                                              setOpenDescription(false)  // Close the popover after selection
                                            }
                                            }

                                          >
                                            <CheckIcon
                                              className={cn(
                                                "mr-2 h-4 w-4",
                                                field.value === description ? "opacity-100" : "opacity-0"
                                              )}
                                            />
                                            {description}
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </FormControl>
                            {/* <FormDescription>Select the manager's email from the list.</FormDescription> */}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div>

                      <FormField
                        control={form.control}
                        name="role"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Role</FormLabel>
                            <FormControl>
                              <Popover open={openRole} onOpenChange={setOpenRole}>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={openRole}
                                    className="w-full h-8 justify-between overflow-hidden"
                                  >
                                    {field.value ? field.value
                                      : "Select role..."}
                                    <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[300px] p-0">
                                  <Command>
                                    <CommandInput placeholder="Search role..." />
                                    <CommandList>
                                      <CommandEmpty>No role found.</CommandEmpty>
                                      <CommandGroup>
                                        {data!.roles.map((role: string) => (
                                          <CommandItem
                                            key={role}
                                            value={role}
                                            onSelect={() => {
                                              field.onChange(role)
                                              setOpenRole(false)  // Close the popover after selection
                                            }
                                            }

                                          >
                                            <CheckIcon
                                              className={cn(
                                                "mr-2 h-4 w-4",
                                                field.value === role ? "opacity-100" : "opacity-0"
                                              )}
                                            />
                                            {role}
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </FormControl>
                            {/* <FormDescription>Select the manager's email from the list.</FormDescription> */}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div >

                      <FormField
                        control={form.control}
                        name="companyId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company</FormLabel>
                            <FormControl>
                              <div className="w-full">

                                <CompanySelector
                                  defaultValue={field.value}
                                  companies={data!.companies}
                                  onSelectCompany={(company: Company | null) => field.onChange(company?.id)}
                                />
                              </div>
                            </FormControl>
                            {/* <FormDescription>Select the manager's email from the list.</FormDescription> */}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div>

                      <FormField
                        control={form.control}
                        name="deadline"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Deadline</FormLabel>
                            <FormControl>
                              <DatePickerWithPresets
                                value={field.value}

                                onChange={(date) => field.onChange(date)}
                              />
                            </FormControl>
                            <FormDescription>Select the deadline for the task.</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div>

                      <FormField
                        control={form.control}
                        name="reminder1"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Reminder 1</FormLabel>
                            <FormControl>
                              <DatePickerWithPresets
                                value={field.value}
                                onChange={(date) => field.onChange(date)}
                              />
                            </FormControl>
                            <FormDescription>Set the first reminder for the task.</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div>

                      <FormField
                        control={form.control}
                        name="reminder2"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Reminder 2</FormLabel>
                            <FormControl>
                              <DatePickerWithPresets
                                value={field.value}
                                onChange={(date) => field.onChange(date)}
                              />
                            </FormControl>
                            <FormDescription>Set the second reminder for the task.</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div>
                      <FormField
                        control={form.control}
                        name="keyDeadline"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>
                                Key Deadline
                              </FormLabel>
                              <FormDescription>
                                Mark this task as a key deadline.
                              </FormDescription>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <Button
                    disabled={loading}
                    type="submit"
                    className="mt-4"
                  >
                    Save
                    {loading && <LoadingSpinner />}
                  </Button>
                </form>
              </Form>
            </CardContent>

          </Card>
          <Card className="md:col-span-1 sm:col-span-3">
            <CardHeader>
              <CardTitle>Task Assignee</CardTitle>

            </CardHeader>
            <CardContent>
              <TaskAddAssignee
                companyId={task!.companyId}
                role={task!.role}
                people={people ?? []}
              />
              <div>
                {(task?.assignees ?? []).map((assignee: string) => (
                  <div key={assignee} className="flex items-center justify-between py-2 border-b">
                    <span

                      className="text-sm font-medium text-gray-900">
                      {assignee}
                    </span>
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={loadingDelete}
                      className="text-xs text-white"
                      onClick={() => handleDeleteRole(assignee)}
                    >
                      <TrashIcon className=" h-2 w-2" />
                      {
                        loadingDelete && <Loader2 className="animate-spin" />
                      }
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

        </div>

      </div>
    </div>
  )
}
